import { css } from '@emotion/react';
import { FC } from 'react';
import getSymbol from 'currency-symbol-map';
import { fontStyles, borderRadius } from '../../styles';
import marker from './marker.svg';

interface Props {
  highPrice: number | null | undefined;
  lowPrice: number | null | undefined;
  markedPrice: number | null | undefined;
  currency?: string | null;
}

const formatPrice = (price: number | null | undefined, currencySymbol = '$'): string => {
  if (price == null || isNaN(price)) return 'N/A';
  if (price < 1) return `${currencySymbol}${price.toFixed(1)}`;
  if (price < 1000) return `${currencySymbol}${price.toFixed(0)}`;
  if (price < 100000) return `${currencySymbol}${(price / 1000).toFixed(1)}K`;
  return `${currencySymbol}${(price / 1000).toFixed(0)}\u200AK`;
};

const calculatePricePercent = (
  highPrice: number | null | undefined,
  lowPrice: number | null | undefined,
  markedPrice: number | null | undefined,
) => {
  if (highPrice == null || lowPrice == null || markedPrice == null) return 0;
  if (isNaN(highPrice) || isNaN(lowPrice) || isNaN(markedPrice)) return 0;
  if (markedPrice > highPrice) return 100;
  if (markedPrice < lowPrice) return 0;
  return ((markedPrice - lowPrice) / (highPrice - lowPrice)) * 100;
};

const PriceRange: FC<Props> = ({ highPrice, lowPrice, markedPrice, currency }) => {
  const currencySymbol = currency ? getSymbol(currency) || '$' : '$';
  // If any of the required prices are missing, show a fallback
  if (highPrice == null || lowPrice == null || markedPrice == null) {
    return (
      <div
        css={css`
          ${fontStyles.roboMite};
          color: var(--secondary-text);
          font-size: 12px;
        `}
      >
        Price data unavailable
      </div>
    );
  }

  const pricePercent = calculatePricePercent(highPrice, lowPrice, markedPrice);
  const styles = {
    base: css`
      width: 100%;
      color: var(--text-color);
      ${fontStyles.roboMite};
    `,
    prices: css`
      display: flex;
      justify-content: space-between;
      margin-top: 4px;
      padding: 0 4px;
    `,
    line: css`
      flex: 1;
      background-color: var(--border-color);
      border-radius: ${borderRadius};
      height: 4px;
      position: relative;
      margin: 0 6px;
    `,
    marker: css`
      width: 12px;
      position: absolute;
      top: -4px;
      left: calc(${pricePercent}% - 6px);
      filter: var(--icon-filter);
    `,
  };

  return (
    <div css={styles.base}>
      <div css={styles.line}>
        <img src={marker} css={styles.marker} />
      </div>
      <div css={styles.prices}>
        <span>{formatPrice(lowPrice, currencySymbol)}</span>
        <span>{formatPrice(highPrice, currencySymbol)}</span>
      </div>
    </div>
  );
};

export default PriceRange;
